/**
 * Where the pre-signup handoff lives between the start page and the product that
 * gets created after the visitor confirms their email.
 *
 * `sessionStorage` alone is not enough any more. Signup now ends with a
 * confirmation email, and its link opens a NEW TAB — a fresh session, where the
 * vision and the draft token that go with it would both be gone, so the product
 * would be created from nothing. Every value is therefore written to both stores
 * and read back from either, with `sessionStorage` winning because it is the one
 * that cannot leak between two people using the same browser.
 *
 * The localStorage copy carries an expiry for the same reason: it outlives the tab
 * that wrote it, so it must not outlive the visit by much. A day is long enough for
 * someone to confirm their email in the evening and short enough that a stale idea
 * never attaches itself to a later signup. It is cleared the moment the draft is
 * claimed, which is the real end of its life.
 */
const TTL_MS = 24 * 60 * 60 * 1000;

type Envelope = { value: string; expires_at: number };

function localKey(key: string): string {
  return `${key}:persisted`;
}

/** Both stores, so a value survives both a reload and a new tab. */
export function writeHandoffValue(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage disabled (private mode) — the localStorage copy may still work */
  }

  try {
    const envelope: Envelope = { value, expires_at: Date.now() + TTL_MS };
    localStorage.setItem(localKey(key), JSON.stringify(envelope));
  } catch {
    /* ignore */
  }
}

/**
 * This tab only, dropping any persisted copy an earlier visit left behind. For values
 * that should outlive the tab only once we know who is holding them — see
 * `persistHandoffValue`.
 */
export function writeSessionHandoffValue(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }

  try {
    localStorage.removeItem(localKey(key));
  } catch {
    /* ignore */
  }
}

/** Give the value this tab holds, if any, the persisted copy `writeHandoffValue` makes. */
export function persistHandoffValue(key: string): void {
  let value: string | null = null;
  try {
    value = sessionStorage.getItem(key);
  } catch {
    return;
  }
  if (value) writeHandoffValue(key, value);
}

/** This tab first, then the copy a previous tab left behind, if it is still fresh. */
export function readHandoffValue(key: string): string | null {
  try {
    const current = sessionStorage.getItem(key);
    if (current) return current;
  } catch {
    /* fall through to the persisted copy */
  }

  const persisted = readPersisted(key);
  if (persisted === null) return null;

  // Bring it back into this tab so the rest of the visit behaves like any other.
  try {
    sessionStorage.setItem(key, persisted);
  } catch {
    /* ignore */
  }

  return persisted;
}

export function clearHandoffValue(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }

  try {
    localStorage.removeItem(localKey(key));
  } catch {
    /* ignore */
  }
}

function readPersisted(key: string): string | null {
  try {
    const raw = localStorage.getItem(localKey(key));
    if (!raw) return null;

    const envelope = JSON.parse(raw) as Partial<Envelope>;
    if (typeof envelope?.value !== 'string') return null;

    if (
      typeof envelope.expires_at !== 'number' ||
      envelope.expires_at <= Date.now()
    ) {
      localStorage.removeItem(localKey(key));
      return null;
    }

    return envelope.value;
  } catch {
    return null;
  }
}
